import React, { useState, useEffect } from 'react';
import {
  getCustomSections,
  createCustomSection,
  updateCustomSection,
  deleteCustomSection,
  addCustomSectionItem,
  updateCustomSectionItem,
  deleteCustomSectionItem
} from '../../services/profile';
import { FaEdit, FaTrashAlt, FaPlus, FaTimes, FaSave, FaPuzzlePiece, FaEllipsisH } from 'react-icons/fa';
import './CustomSection.css';

const CustomSection = (props) => {
  const { profileSection, profileData, setCompletion } = props;
  const [customSectionsList, setCustomSectionsList] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(null);
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // State for new/editing custom section
  const [sectionForm, setSectionForm] = useState({
    section_name: '',
    items: [{ title: '', subtitle: '', description: '', start_date: '', end_date: '' }]
  });

  // Fetch custom sections on component mount
  useEffect(() => {
    const fetchCustomSections = async () => {
      try {
        setIsLoading(true);
        const response = await getCustomSections(profileData?.profile_id);
        setCustomSectionsList(response);
      } catch (error) {
        console.error('Error fetching custom sections:', error);
        setFormError('Failed to load custom sections. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomSections();
  }, []);

  useEffect(() => {
    setCompletion("custom", customSectionsList);
  }, [customSectionsList])


  // Clear form data
  const clearForm = () => {
    setSectionForm({
      section_name: '',
      items: [{ title: '', subtitle: '', description: '', start_date: '', end_date: '' }]
    });
    setFormError('');
  };

  // Handle section form input change
  const handleSectionInputChange = (e) => {
    const { name, value } = e.target;
    setSectionForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle item form input change
  const handleItemInputChange = (e, index) => {
    const { name, value } = e.target;
    setSectionForm(prev => {
      const updatedItems = [...prev.items];
      updatedItems[index] = {
        ...updatedItems[index],
        [name]: value
      };
      return {
        ...prev,
        items: updatedItems
      };
    });
  };

  // Add new item field
  const handleAddItemField = () => {
    setSectionForm(prev => ({
      ...prev,
      items: [...prev.items, { title: '', subtitle: '', description: '', start_date: '', end_date: '' }]
    }));
  };

  // Remove item field
  const handleRemoveItemField = (index) => {
    setSectionForm(prev => {
      const updatedItems = [...prev.items];
      updatedItems.splice(index, 1);
      return {
        ...prev,
        items: updatedItems.length ? updatedItems : [{ title: '', subtitle: '', description: '', start_date: '', end_date: '' }]
      };
    });
  };

  // Add new custom section
  const handleAddSection = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!sectionForm.section_name) {
      setFormError('Please provide a section name');
      return;
    }

    if (!sectionForm.items.some(item => item.title)) {
      setFormError('Please add at least one item with a title');
      return;
    }

    try {
      // Format data for API - first create the section
      const sectionData = {
        section_name: sectionForm.section_name,
        items: []
      };

      // Send to API to create section
      const newSection = await createCustomSection(sectionData, profileData?.profile_id);

      // Now add items to the section
      const itemsWithContent = sectionForm.items.filter(item => item.title);
      const itemPromises = itemsWithContent.map(item => {
        return addCustomSectionItem(newSection.id, {
          title: item.title,
          subtitle: item.subtitle || null,
          description: item.description || null,
          start_date: item.start_date || null,
          end_date: item.end_date || null
        });
      });

      // Wait for all items to be added
      const addedItems = await Promise.all(itemPromises);

      // Update local state with the new section and its items
      setCustomSectionsList(prev => [...prev, {
        ...newSection,
        items: addedItems
      }]);

      // Reset form and state
      clearForm();
      setIsAdding(false);
    } catch (error) {
      console.error('Error adding custom section:', error);
      setFormError('Failed to add custom section. Please try again.');
    }
  };

  // Start editing a section
  const handleEditStart = (section) => {
    setIsEditing(section.id);

    // Format the section data for the form
    setSectionForm({
      section_name: section.section_name,
      items: section.items.length > 0 ? section.items.map(item => ({
        id: item.id,
        title: item.title || '',
        subtitle: item.subtitle || '',
        description: item.description || '',
        start_date: item.start_date || '',
        end_date: item.end_date || ''
      })) : [{ title: '', subtitle: '', description: '', start_date: '', end_date: '' }]
    });
  };

  // Save edited section
  const handleEditSave = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!sectionForm.section_name) {
      setFormError('Please provide a section name');
      return;
    }

    try {
      // Update the section name
      const sectionData = {
        section_name: sectionForm.section_name
      };

      // Update section in API
      await updateCustomSection(isEditing, sectionData, profileData?.profile_id);

      // Get the existing section to compare items
      const existingSection = customSectionsList.find(section => section.id === isEditing);
      const existingItems = existingSection?.items || [];

      // Track all API calls
      const apiCalls = [];

      // Handle existing items (update or delete)
      for (const existingItem of existingItems) {
        const matchingItem = sectionForm.items.find(item => item.id === existingItem.id);

        if (matchingItem) {
          // Item exists in the form, update it if changed
          if (matchingItem.title !== existingItem.title ||
            matchingItem.subtitle !== existingItem.subtitle ||
            matchingItem.description !== existingItem.description ||
            matchingItem.start_date !== existingItem.start_date ||
            matchingItem.end_date !== existingItem.end_date) {

            apiCalls.push(updateCustomSectionItem(existingItem.id, {
              title: matchingItem.title,
              subtitle: matchingItem.subtitle || null,
              description: matchingItem.description || null,
              start_date: matchingItem.start_date || null,
              end_date: matchingItem.end_date || null
            }, profileData?.profile_id));
          }
        } else {
          // Item not in form anymore, delete it
          apiCalls.push(deleteCustomSectionItem(existingItem.id, profileData?.profile_id));
        }
      }

      // Handle new items (add)
      for (const formItem of sectionForm.items) {
        if (!formItem.id && formItem.title) {
          // This is a new item with content, add it
          apiCalls.push(addCustomSectionItem(isEditing, {
            title: formItem.title,
            subtitle: formItem.subtitle || null,
            description: formItem.description || null,
            start_date: formItem.start_date || null,
            end_date: formItem.end_date || null
          }, profileData?.profile_id));
        }
      }

      // Execute all API calls
      await Promise.all(apiCalls);

      // Fetch the updated section to get all changes
      const updatedSections = await getCustomSections(profileData?.profile_id);
      setCustomSectionsList(updatedSections);

      // Reset form and state
      clearForm();
      setIsEditing(null);
    } catch (error) {
      console.error('Error updating custom section:', error);
      setFormError('Failed to update custom section. Please try again.');
    }
  };

  // Cancel adding/editing
  const handleCancel = () => {
    clearForm();
    setIsAdding(false);
    setIsEditing(null);
  };

  // Delete section
  const handleDeleteSection = async (id) => {
    if (window.confirm('Are you sure you want to delete this section? This will also delete all items within this section.')) {
      try {
        await deleteCustomSection(id, profileData?.profile_id);
        setCustomSectionsList(prev => prev.filter(section => section.id !== id));
      } catch (error) {
        console.error('Error deleting custom section:', error);
        setFormError('Failed to delete custom section. Please try again.');
      }
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="custom-sections-loading">
        <div className="loading-spinner"></div>
        <p>Loading custom sections...</p>
      </div>
    );
  }

  return (
    <div className="custom-sections-section">
      <div className="section-header">
        <h2><div className="section-icon">
          {profileSection.icon}
        </div> Custom Sections</h2>
        {!isAdding && !isEditing && (
          <button
            onClick={() => setIsAdding(true)}
            className="btn-add"
          >
            <FaPlus /> Add Custom Section
          </button>
        )}
      </div>

      <p className="section-description">
        Add custom sections to your resume that don't fit into the standard categories, such as languages, publications, patents, etc.
      </p>

      {/* Custom Sections List */}
      {customSectionsList.length > 0 ? (
        <div className="custom-sections-list">
          {customSectionsList.map((section) => (
            <div key={section.id} className="custom-section-item">
              {isEditing === section.id ? (
                <form onSubmit={handleEditSave} className="custom-section-form">
                  <h3>Edit Section</h3>
                  {formError && <div className="form-error">{formError}</div>}

                  <div className="form-group">
                    <label htmlFor="section_name">Section Title *</label>
                    <input
                      type="text"
                      id="section_name"
                      name="section_name"
                      value={sectionForm.section_name}
                      onChange={handleSectionInputChange}
                      required
                      placeholder="e.g., Languages, Publications, Patents"
                      className="form-control"
                    />
                  </div>

                  <div className="section-items">
                    <h4>Section Items</h4>
                    {sectionForm.items.map((item, index) => (
                      <div key={index} className="section-item-form">
                        <div className="item-header">
                          <h5>Item {index + 1}</h5>
                          {sectionForm.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveItemField(index)}
                              className="btn-icon delete"
                              title="Remove Item"
                            >
                              <FaTrashAlt />
                            </button>
                          )}
                        </div>

                        <div className="form-group">
                          <label htmlFor={`title-${index}`}>Title *</label>
                          <input
                            type="text"
                            id={`title-${index}`}
                            name="title"
                            value={item.title}
                            onChange={(e) => handleItemInputChange(e, index)}
                            placeholder="Item title"
                            className="form-control"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor={`subtitle-${index}`}>Subtitle</label>
                          <input
                            type="text"
                            id={`subtitle-${index}`}
                            name="subtitle"
                            value={item.subtitle}
                            onChange={(e) => handleItemInputChange(e, index)}
                            placeholder="Item subtitle or organization"
                            className="form-control"
                          />
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor={`start_date-${index}`}>Start Date</label>
                            <input
                              type="date"
                              id={`start_date-${index}`}
                              name="start_date"
                              value={item.start_date}
                              onChange={(e) => handleItemInputChange(e, index)}
                              className="form-control"
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor={`end_date-${index}`}>End Date</label>
                            <input
                              type="date"
                              id={`end_date-${index}`}
                              name="end_date"
                              value={item.end_date}
                              onChange={(e) => handleItemInputChange(e, index)}
                              className="form-control"
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor={`description-${index}`}>Description</label>
                          <textarea
                            id={`description-${index}`}
                            name="description"
                            value={item.description}
                            onChange={(e) => handleItemInputChange(e, index)}
                            rows="3"
                            placeholder="Additional details about this item"
                            className="form-control"
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={handleAddItemField}
                      className="btn-secondary add-item"
                    >
                      <FaPlus /> Add Another Item
                    </button>
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={handleCancel} className="btn-secondary">
                      <FaTimes /> Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      <FaSave /> Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                <div className="custom-section-display">
                  <div className="custom-section-header">
                    <h4>{section.section_name}</h4>
                    <div className="custom-section-actions">
                      <button
                        onClick={() => handleEditStart(section)}
                        className="btn-icon edit"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDeleteSection(section.id)}
                        className="btn-icon delete"
                        title="Delete"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </div>

                  <div className="custom-section-content">
                    {section.items.length > 0 ? (
                      <div className="section-items-display">
                        {section.items.map((item) => (
                          <div key={item.id} className="section-item">
                            <div className="item-header">
                              <h5>{item.title}</h5>
                              {item.subtitle && <h6>{item.subtitle}</h6>}
                              {(item.start_date || item.end_date) && (
                                <p className="item-dates">
                                  {formatDate(item.start_date)}
                                  {item.start_date && item.end_date && ' - '}
                                  {formatDate(item.end_date)}
                                </p>
                              )}
                            </div>
                            {item.description && (
                              <p className="item-description">{item.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-items">No items in this section yet.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-list">
          <p>No custom sections added yet.</p>
          <p>Click "Add Custom Section" to get started.</p>
        </div>
      )}

      {/* Add Custom Section Form */}
      {isAdding && (
        <div className="add-custom-section">
          <form onSubmit={handleAddSection} className="custom-section-form">
            <h3>Add New Section</h3>
            {formError && <div className="form-error">{formError}</div>}

            <div className="form-group">
              <label htmlFor="section_name">Section Title *</label>
              <input
                type="text"
                id="section_name"
                name="section_name"
                value={sectionForm.section_name}
                onChange={handleSectionInputChange}
                required
                placeholder="e.g., Languages, Publications, Patents"
                className="form-control"
              />
            </div>

            <div className="section-items">
              <h4>Section Items</h4>
              {sectionForm.items.map((item, index) => (
                <div key={index} className="section-item-form">
                  <div className="item-header">
                    <h5>Item {index + 1}</h5>
                    {sectionForm.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveItemField(index)}
                        className="btn-icon delete"
                        title="Remove Item"
                      >
                        <FaTrashAlt />
                      </button>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor={`title-${index}`}>Title *</label>
                    <input
                      type="text"
                      id={`title-${index}`}
                      name="title"
                      value={item.title}
                      onChange={(e) => handleItemInputChange(e, index)}
                      placeholder="Item title"
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor={`subtitle-${index}`}>Subtitle</label>
                    <input
                      type="text"
                      id={`subtitle-${index}`}
                      name="subtitle"
                      value={item.subtitle}
                      onChange={(e) => handleItemInputChange(e, index)}
                      placeholder="Item subtitle or organization"
                      className="form-control"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor={`start_date-${index}`}>Start Date</label>
                      <input
                        type="date"
                        id={`start_date-${index}`}
                        name="start_date"
                        value={item.start_date}
                        onChange={(e) => handleItemInputChange(e, index)}
                        className="form-control"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`end_date-${index}`}>End Date</label>
                      <input
                        type="date"
                        id={`end_date-${index}`}
                        name="end_date"
                        value={item.end_date}
                        onChange={(e) => handleItemInputChange(e, index)}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor={`description-${index}`}>Description</label>
                    <textarea
                      id={`description-${index}`}
                      name="description"
                      value={item.description}
                      onChange={(e) => handleItemInputChange(e, index)}
                      rows="3"
                      placeholder="Additional details about this item"
                      className="form-control"
                    />
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={handleAddItemField}
                className="btn-secondary add-item"
              >
                <FaPlus /> Add Another Item
              </button>
            </div>

            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="btn-secondary">
                <FaTimes /> Cancel
              </button>
              <button type="submit" className="btn-primary">
                <FaPlus /> Add Section
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Examples Section */}
      {!isAdding && !isEditing && customSectionsList.length === 0 && (
        <div className="custom-section-examples">
          <h4>Examples of Custom Sections</h4>
          <div className="examples-list">
            <div className="example-item">
              <h5>Languages</h5>
              <p>English (Native), Spanish (Fluent), French (Intermediate)</p>
            </div>
            <div className="example-item">
              <h5>Publications</h5>
              <p>"Title of Publication" in Journal Name (Year)<br />
                "Another Publication Title" in Conference Name (Year)</p>
            </div>
            <div className="example-item">
              <h5>Professional Affiliations</h5>
              <p>Member, Association Name (Year-Present)<br />
                Committee Member, Organization Name (Year-Year)</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomSection;